import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateFavoriteLocally, updateWishlist, selectFavorites } from '../../redux/permissionsSlice';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import PushPinIcon from '@mui/icons-material/PushPin';
import Diversity2Icon from '@mui/icons-material/Diversity2';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import AirlineSeatFlatAngledIcon from '@mui/icons-material/AirlineSeatFlatAngled';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  position: 'relative',
  border: "1px solid #e0e0e0",
  boxShadow: "none",
}));

const FavoritesComponent = () => {
  const dispatch = useDispatch();
  const favorites = useSelector(selectFavorites);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFavorite, setSelectedFavorite] = useState(null);

  const handlePinClick = (e, favorite) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!favorite.UserPrevId) return;
    
    // Set the selected favorite and open confirmation dialog
    setSelectedFavorite(favorite);
    setOpenDialog(true);
  };

  const handleConfirmUnpin = () => {
    if (!selectedFavorite) return;
    
    // Since it's in favorites, clicking will unpin it (set wishlist to false)
    const newWishlistStatus = false;
    
    // Optimistic update
    dispatch(updateFavoriteLocally({ UserPrevId: selectedFavorite.UserPrevId, wishlist: newWishlistStatus }));
    
    // API call
    dispatch(updateWishlist({ UserPrevId: selectedFavorite.UserPrevId, wishlist: newWishlistStatus }));
    
    // Reset state
    setOpenDialog(false);
    setSelectedFavorite(null);
  };

  const handleCancelUnpin = () => {
    setOpenDialog(false);
    setSelectedFavorite(null);
  };

  if (favorites.length === 0) {
    return (
      <Box sx={{ flexGrow: 1, p: 2 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6">No favorites added yet</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ marginBottom: 2, fontWeight: 'bold' }}>
        My Favorites
      </Typography>
      <Grid container spacing={2}>
        {favorites.map((favorite) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={favorite.UserPrevId}>
            <Link to={favorite.link} style={{ textDecoration: 'none' }}>
              <Item>
                <Box sx={{
                  textAlign: "center",
                  fontWeight: 'bold',
                  fontSize: { xs: '14px', sm: '16px', md: '18px', lg: '18px' },
                  p: 1
                }}>
                  <span style={{ color: '#635bff', fontSize: '16px' }}>
                    {favorite.ModuleName}
                  </span>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px 16px' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <Diversity2Icon style={{ color: '#dddbff', marginRight: '8px' }} />
                      <span style={{ color: '#000' }}>Total Count: {favorite.TotalCount || 10}</span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <DirectionsRunIcon style={{ color: '#dddbff', marginRight: '8px' }} />
                      <span style={{ color: '#000' }}>Active Count: {favorite.ActiveCount || 10}</span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AirlineSeatFlatAngledIcon style={{ color: '#dddbff', marginRight: '8px' }} />
                      <span style={{ color: '#000' }}>InActive Count: {favorite.InactiveCount || 0}</span>
                    </Box>
                  </Box>
                  
                  <IconButton
                    onClick={(e) => handlePinClick(e, favorite)}
                    sx={{
                      alignSelf: 'center',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      }
                    }}
                  >
                    <PushPinIcon
                      sx={{
                        width: {
                          xs: "30px",
                          sm: "35px",
                          md: "40px",
                          lg: "40px"
                        },
                        height: {
                          xs: "30px",
                          sm: "35px",
                          md: "40px",
                          lg: "40px"
                        },
                        color: '#635bff',
                        borderRadius: "50%",
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                        transition: 'transform 0.3s',
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
                      onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    />
                  </IconButton>
                </Box>
              </Item>
            </Link>
          </Grid>
        ))}
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCancelUnpin}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Unpin"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to unpin "{selectedFavorite?.ModuleName}" from your favorites?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUnpin} color="primary">
            No
          </Button>
          <Button onClick={handleConfirmUnpin} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FavoritesComponent;